import React, { useEffect, useRef, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { ParamsType, InitialState, ModuleLessonType } from './types'
import { BlockType } from '@famiryru/react-notion-renderer/src/types'
import { useDispatch, useSelector } from 'react-redux'
import { useAlert } from 'react-alert'
import * as actions from './actions'
import { ThemeType } from '../CoursePage/types'

// Styles
import './styles.scss'

// Components
import Progress from '../../components/ProgressBlock'
import Header from '../../components/Header'
import Footer from '../../components/Footer'
import LoadingPage from '../../components/LoadingPage'
import ShortDescriptionBlock from '../../components/ShortDescriptionBlock'
import ModuleButtonPrev from '../../components/ModuleButtonPrev'
import ModuleButtonNext from '../../components/ModuleButtonNext'
import ModuleMarker from '../../components/ModuleMarker'
import ModuleHelperBlock from '../../components/ModuleHelperBlock'

export default () => {
  const [lessons, setLessons] = useState<ModuleLessonType[]>([])
  const [selectedLesson, setSelectedLesson] = useState<string>('')
  const [shortDescription, setShortDescription] = useState<BlockType[]>([])
  const [isLessonBlocked, setIsLessonBlocked] = useState<boolean>(false)
  const [dataSelectedLesson, setDataSelectedLesson] = useState<
    ModuleLessonType | undefined
  >(undefined)
  const [themes, setThemes] = useState<ThemeType[] | null>(null)

  const { id, mid } = useParams<ParamsType>()
  const dispatch = useDispatch()
  const history = useHistory()
  const alert = useAlert()
  const isMounted = useRef<boolean>(false)

  useEffect(() => {
    dispatch(actions.destroy())
    dispatch(actions.fetchModulePage(id, mid))
    isMounted.current = true
    return () => {
      dispatch(actions.destroy())
    }
  }, [])

  const { page, isFetching, error, progress, isProcessUserState } = useSelector(
    (state: { modulePage: InitialState }) => state.modulePage
  )

  useEffect(() => {
    if (error) {
      alert.error(error)
    }
  }, [error])

  const indexSelectedLesson = lessons?.find(
    lesson => lesson.lesson_id === selectedLesson
  )?.index_number

  useEffect(() => {
    if (selectedLesson) {
      const lessonData = lessons?.find(
        lesson => lesson.lesson_id === selectedLesson
      )
      setDataSelectedLesson(lessonData)
      const notionPageContent = lessonData?.short_description?.find(
        notion => notion.tag === 'short'
      )?.notion?.notion_page_content
      const checkBlockedLesson =
        lessonData?.is_blocked || !lessonData?.is_available_on_time

      setIsLessonBlocked(checkBlockedLesson)
      setShortDescription(
        notionPageContent ? JSON.parse(notionPageContent) : []
      )
    }
  }, [selectedLesson])

  useEffect(() => {
    if (page?.lessons?.length) {
      const markerLessons: ModuleLessonType[] = []
      const themesData: ThemeType[] = []

      page.lessons.forEach(lesson => {
        if (
          !themesData.some(theme => theme.theme_id === lesson.theme.theme_id)
        ) {
          themesData.push(lesson.theme)
        }
        lesson.module_lessons
          .filter(moduleLesson => moduleLesson.markers.length)
          .forEach(moduleLesson => {
            markerLessons.push({
              ...moduleLesson,
              title: lesson.title,
              blocks: lesson.blocks,
              theme: lesson.theme,
              type: moduleLesson.markers[0].type,
              short_description: lesson.short_description,
            })
          })
      })

      const sortLessons = (arr: ModuleLessonType[]): ModuleLessonType[] => {
        const sorted = [...arr.sort((a, b) => a.position - b.position)]

        sorted.forEach((item, index) => {
          if (item.is_first) {
            sorted.splice(index, 1)
            sorted.unshift(item)
            return
          }
          if (item.is_last) {
            sorted.splice(index, 1)
            sorted.push(item)
          }
        })

        return sorted
      }

      const sortedMarkerLessons = sortLessons(markerLessons)

      const continueLessonId = sortedMarkerLessons.find(
        lesson => (lesson.is_joined && lesson.can_join) || !lesson.is_finished
      )?.lesson_id

      if (continueLessonId) {
        setSelectedLesson(continueLessonId)
      }

      setThemes([...themesData])

      setLessons([...sortedMarkerLessons])
    }
  }, [page])

  const handleJoinLesson = () => {
    if (dataSelectedLesson?.is_joined) {
      history.push(`/courses/${id}/modules/${mid}/lessons/${selectedLesson}`)
    } else {
      dispatch(actions.joinLesson(id, mid, selectedLesson))
    }
  }

  return (
    <>
      <Header />
      <div className={'container'}>
        {!isMounted.current || isFetching || isProcessUserState ? (
          <LoadingPage />
        ) : (
          <>
            {progress?.progress?.other?.percent ? (
              <Progress
                title={'Прогресс курса: '}
                percent={progress.progress?.other?.percent}
                style={{ marginBottom: 44 }}
              />
            ) : null}
            <div className={'module__outer'}>
              <div className={'module__title'}>
                <span>{page?.title}</span>
                <div className={'choice'}>Выбери урок!</div>
              </div>
              <div className={'module__lessons'}>
                {lessons.map((lesson, index) => (
                  <ModuleMarker
                    key={lesson.lesson_id}
                    lessonId={lesson.lesson_id}
                    index={lesson.index_number}
                    active={selectedLesson === lesson.lesson_id}
                    onClick={id => setSelectedLesson(id)}
                    withSeparator={
                      !!(index && ++index % 9 === 0 && lessons[index])
                    }
                    type={lesson.type}
                    color={lesson.theme?.color || '#A3A3A3'}
                    isBlocked={lesson.is_blocked}
                    isFinished={lesson.is_finished}
                    isAvailable={lesson.is_available_on_time}
                    isJoined={lesson.is_joined}
                  />
                ))}
              </div>
              <ModuleHelperBlock themes={themes} />
            </div>
            {selectedLesson ? (
              <ShortDescriptionBlock
                title={`Описание урока #${indexSelectedLesson}`}
                isAvailable={!!dataSelectedLesson?.is_available_on_time}
                isBlockingTime={dataSelectedLesson?.available_at}
                blocks={shortDescription}
                style={{ marginBottom: 34 }}
                onComplete={() => dispatch(actions.fetchModulePage(id, mid))}
              />
            ) : null}
            <div className={'buttons-module-container'}>
              <ModuleButtonPrev
                onClick={() => history.push(`/courses/${id}`)}
                title={'Хочу назад!'}
              />
              <ModuleButtonNext
                disabled={isLessonBlocked || !selectedLesson}
                onClick={handleJoinLesson}
                title={'Иду сюда!'}
              />
            </div>
            {error ? <span style={{ color: 'red' }}>{error}</span> : null}
            {progress?.progress?.other?.percent ? (
              <Progress
                title={'Прогресс курса: '}
                percent={progress.progress?.other?.percent}
                style={{ marginBottom: 37 }}
              />
            ) : null}
          </>
        )}
      </div>
      <Footer />
    </>
  )
}
